/**
 * Drawing Toolbar Smart Positioning Geometry.
 *
 * Computes on-screen floating coordinate for the drawing settings toolbar,
 * preferring above the target point (TradingView style), falling back below
 * if constrained, and clamping within the canvas container.
 */

export interface PopupCoordinates {
  left: number;
  top: number;
}

/**
 * Calculates clamped popup toolbar screen position.
 */
export function calculatePopupPosition(
  targetX: number,
  targetY: number,
  popupWidth: number,
  popupHeight: number,
  containerWidth: number,
  containerHeight: number,
  margin: number = 8,
  gap: number = 14,
): PopupCoordinates {
  const pw = popupWidth || 300;
  const ph = popupHeight || 40;
  const cw = containerWidth || 1000;
  const ch = containerHeight || 600;

  let left = (targetX || 0) - pw / 2;
  let top = (targetY || 0) - ph - gap; // Prefer showing above

  if (top < margin) {
    top = (targetY || 0) + gap; // Not enough room above -> show below
  }

  // Clamping within container boundaries
  if (left < margin) {
    left = margin;
  }
  if (left + pw > cw) {
    left = Math.max(margin, cw - pw - margin);
  }
  if (top + ph > ch) {
    top = Math.max(margin, ch - ph - margin);
  }

  return { left, top };
}

/**
 * Clamps dragged toolbar screen coordinates within the container boundaries.
 */
export function clampDragPosition(
  startLeft: number,
  startTop: number,
  dx: number,
  dy: number,
  containerWidth: number,
  containerHeight: number,
  popupWidth: number,
  popupHeight: number,
): PopupCoordinates {
  let left = startLeft + dx;
  let top = startTop + dy;
  left = Math.max(0, Math.min(containerWidth - popupWidth, left));
  top = Math.max(0, Math.min(containerHeight - popupHeight, top));
  return { left, top };
}
